import React, { useState, useEffect } from 'react';
import { 
    Users, Search, Filter, MoreVertical, Edit, Trash2, 
    Eye, Lock, Unlock, Mail, Phone, Calendar, 
    UserCheck, UserX, Download, Upload, RefreshCw,
    Plus, Settings, Shield, AlertCircle
} from 'lucide-react';
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';
import useApiRequest from './useApiRequest.jsx';

const UserManagementComponent = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: false,
    is_staff: false,
    contact: '',
    gender: '',
    dob: ''
  });
  const [addUserForm, setAddUserForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    is_active: true,
    is_staff: false,
    contact: '',
    gender: '',
    dob: ''
  });

  // Fetch users data
  const { data: userProfilesData, loading: usersLoading, error: usersError } = useFetch(`${domain}/accounts/users`);
  
  // API request methods
  const { putRequest, deleteRequest, postRequest } = useApiRequest();
  
  // Process the data to extract user information from UserProfile objects
  const users = userProfilesData ? userProfilesData.map(profile => ({
    id: profile.owner?.id,
    username: profile.owner?.username,
    email: profile.owner?.email,
    first_name: profile.owner?.first_name,
    last_name: profile.owner?.last_name,
    is_active: profile.owner?.is_active ?? false, // Default to false if not provided
    is_staff: profile.owner?.is_staff,
    is_superuser: profile.owner?.is_superuser,
    date_joined: profile.owner?.date_joined,
    // Additional profile data
    profile_id: profile.id,
    dob: profile.dob,
    gender: profile.gender,
    contact: profile.contact,
    profile_picture: profile.profile
  })) : [];

  const [filteredUsers, setFilteredUsers] = useState([]);

  // Debug logging
  useEffect(() => {
    console.log('=== USER MANAGEMENT DEBUG ===');
    console.log('Raw User Profiles Data:', userProfilesData);
    console.log('Processed Users:', users);
    console.log('Users Loading:', usersLoading);
    console.log('Users Error:', usersError);
    console.log('Users Length:', users?.length);
    
    // Check for authentication issues
    if (usersError) {
      console.error('User Management Error:', usersError);
      if (usersError.status === 401) {
        console.error('Authentication required. Please log in as admin.');
      } else if (usersError.status === 403) {
        console.error('Permission denied. Admin access required.');
      }
    }
    
    if (users && users.length > 0) {
      console.log('First 3 users:', users.slice(0, 3));
      console.log('Active users count:', users.filter(u => u.is_active).length);
      console.log('Users with date_joined:', users.filter(u => u.date_joined).length);
    }
  }, [userProfilesData, users, usersLoading, usersError]);

  useEffect(() => {
    if (users) {
      let filtered = users;
      
      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(user => 
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
             // Apply status filter
       if (filterStatus !== 'all') {
         filtered = filtered.filter(user => {
           if (filterStatus === 'active') return user.is_active;
           if (filterStatus === 'inactive') return !user.is_active;
           return true;
         });
       }
      
      setFilteredUsers(filtered);
    }
  }, [users, searchTerm, filterStatus]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      is_active: user.is_active || false,
      is_staff: user.is_staff || false,
      contact: user.contact || '',
      gender: user.gender || '',
      dob: user.dob || ''
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setIsLoading(true);
      try {
        const response = await deleteRequest(`${domain}/users/${userId}`);
        
        if (response.success) {
          // Remove user from the list
          setFilteredUsers(prev => prev.filter(user => user.id !== userId));
          console.log('User deleted:', userId);
        } else {
          console.error('Failed to delete user:', response.message);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    setIsLoading(true);
    try {
      const response = await putRequest(`${domain}/users/${userId}`, {
        is_active: !currentStatus
      });
      
      if (response.success) {
        // Update user status in the list
        setFilteredUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        ));
        console.log('User status toggled:', userId, !currentStatus);
      } else {
        console.error('Failed to toggle user status:', response.message);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async () => {
    setIsLoading(true);
    try {
      const response = await postRequest(`${domain}/accounts/signup`, {
        // User model fields
        username: addUserForm.username,
        first_name: addUserForm.first_name,
        last_name: addUserForm.last_name,
        email: addUserForm.email,
        password: addUserForm.password,
        is_active: addUserForm.is_active,
        is_staff: addUserForm.is_staff,
        // UserProfile model fields
        contact: addUserForm.contact,
        gender: addUserForm.gender,
        dob: addUserForm.dob
      });
      
      if (response.success) {
        // Add new user to the list
        const newUser = response.body;
        setFilteredUsers(prev => [newUser, ...prev]);
        setShowAddUserModal(false);
        setAddUserForm({
          username: '',
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          is_active: true,
          is_staff: false,
          contact: '',
          gender: '',
          dob: ''
        });
        console.log('User created successfully:', newUser);
      } else {
        console.error('Failed to create user:', response.message);
      }
    } catch (error) {
      console.error('Error creating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setIsLoading(true);
    try {
      const response = await putRequest(`${domain}/users/${editingUser.id}`, {
        // User model fields
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        is_active: editForm.is_active,
        is_staff: editForm.is_staff,
        // UserProfile model fields
        contact: editForm.contact,
        gender: editForm.gender,
        dob: editForm.dob
      });
      
      if (response.success) {
        // Update user in the list
        setFilteredUsers(prev => prev.map(user => 
          user.id === editingUser.id ? { ...user, ...response.body } : user
        ));
        setShowUserModal(false);
        setEditingUser(null);
        console.log('User updated successfully:', response.body);
      } else {
        console.error('Failed to update user:', response.message);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportUsers = () => {
    const dataStr = JSON.stringify(filteredUsers, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };



  const getStatusBadge = (user) => {
    if (!user.is_active) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Inactive</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Active</span>;
  };

  const getRoleBadge = (user) => {
    if (user.is_superuser) {
      return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Admin</span>;
    }
    if (user.is_staff) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Staff</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">User</span>;
  };

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Error Display */}
      {usersError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Access Error</h3>
              <p className="text-sm text-red-600">
                {usersError.status === 401 
                  ? 'Authentication required. Please log in as an administrator.'
                  : usersError.status === 403
                  ? 'Permission denied. Admin access required to view user management.'
                  : usersError.message || 'Failed to load user data.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">User Management</h2>
          <p className="text-gray-600">Manage user accounts and permissions</p>
        </div>
                 <div className="flex items-center gap-3">
           <button
             onClick={handleExportUsers}
             className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
           >
             <Download className="w-4 h-4" />
             Export
           </button>
           <button
             onClick={() => setShowAddUserModal(true)}
             className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
           >
             <Plus className="w-4 h-4" />
             Add User
           </button>
         </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">
                {usersLoading ? '...' : (users?.length || 0)}
              </p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">
                {usersLoading ? '...' : (users?.filter(u => u.is_active)?.length || 0)}
              </p>
              <p className="text-sm text-gray-600">Active Users</p>
            </div>
          </div>
        </div>

                 <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
           <div className="flex items-center gap-3 mb-4">
             <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
               <Shield className="w-6 h-6 text-yellow-600" />
             </div>
             <div>
               <p className="text-sm font-bold text-gray-800">
                 {usersLoading ? '...' : (users?.filter(u => !u.is_active)?.length || 0)}
               </p>
               <p className="text-sm text-gray-600">Inactive Users</p>
             </div>
           </div>
         </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">
                {usersLoading ? '...' : (users?.filter(u => u.is_staff || u.is_superuser)?.length || 0)}
              </p>
              <p className="text-sm text-gray-600">Admin Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
                         <select
               value={filterStatus}
               onChange={handleFilterChange}
               className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
             >
               <option value="all">All Users</option>
               <option value="active">Active</option>
               <option value="inactive">Inactive</option>
             </select>
            <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              Users ({filteredUsers.length})
            </h3>
            {selectedUsers.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedUsers.length} selected
                </span>
                <button className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-sm font-medium transition-colors duration-200">
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <div className="min-w-full inline-block align-middle">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                    </td>
                                         <td className="px-6 py-4">
                       <div className="flex items-center">
                         <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                           {user.profile_picture && user.profile_picture !== "/profilepics/user.png" ? (
                             <img 
                               src={domain+user.profile_picture} 
                               alt="Profile" 
                               className="w-full h-full object-cover"
                             />
                           ) : (
                             <Users className="w-5 h-5 text-gray-600" />
                           )}
                         </div>
                         <div className="ml-4">
                           <div className="text-sm font-medium text-gray-900">
                             {user.first_name} {user.last_name}
                           </div>
                           <div className="text-sm text-gray-500">{user.email}</div>
                           {user.contact && (
                             <div className="text-xs text-gray-400">{user.contact}</div>
                           )}
                         </div>
                       </div>
                     </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(user)}
                    </td>
                                         <td className="px-6 py-4 text-sm text-gray-500">
                       {user.date_joined ? new Date(user.date_joined).toLocaleDateString('en-US', {
                         year: 'numeric',
                         month: 'short',
                         day: 'numeric'
                       }) : 'N/A'}
                     </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                          title="Edit user"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          {user.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Edit User</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* User Model Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                                         <input
                       type="text"
                       value={editForm.first_name}
                       onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                       placeholder="Enter first name"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                                         <input
                       type="text"
                       value={editForm.last_name}
                       onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                       placeholder="Enter last name"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                                     <input
                     type="email"
                     value={editForm.email}
                     onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                     placeholder="Enter email address"
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                   />
                </div>

                {/* UserProfile Model Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                                         <input
                       type="text"
                       value={editForm.contact}
                       onChange={(e) => setEditForm({...editForm, contact: e.target.value})}
                       placeholder="Enter phone number"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      value={editForm.gender}
                      onChange={(e) => setEditForm({...editForm, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={editForm.dob}
                    onChange={(e) => setEditForm({...editForm, dob: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* User Permissions */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.is_staff}
                      onChange={(e) => setEditForm({...editForm, is_staff: e.target.checked})}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Staff</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setEditingUser(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveUser}
                disabled={isLoading}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
                 </div>
       )}

       {/* Add User Modal */}
       {showAddUserModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="px-6 py-4 border-b border-gray-200">
               <h3 className="text-lg font-semibold text-gray-800">Add New User</h3>
             </div>
             <div className="p-6">
               <div className="space-y-4">
                 {/* Username and Password */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Username *
                     </label>
                     <input
                       type="text"
                       value={addUserForm.username}
                       onChange={(e) => setAddUserForm({...addUserForm, username: e.target.value})}
                       placeholder="Enter username"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Password *
                     </label>
                     <input
                       type="password"
                       value={addUserForm.password}
                       onChange={(e) => setAddUserForm({...addUserForm, password: e.target.value})}
                       placeholder="Enter password"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     />
                   </div>
                 </div>

                 {/* User Model Fields */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       First Name *
                     </label>
                     <input
                       type="text"
                       value={addUserForm.first_name}
                       onChange={(e) => setAddUserForm({...addUserForm, first_name: e.target.value})}
                       placeholder="Enter first name"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Last Name *
                     </label>
                     <input
                       type="text"
                       value={addUserForm.last_name}
                       onChange={(e) => setAddUserForm({...addUserForm, last_name: e.target.value})}
                       placeholder="Enter last name"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     />
                   </div>
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Email *
                   </label>
                   <input
                     type="email"
                     value={addUserForm.email}
                     onChange={(e) => setAddUserForm({...addUserForm, email: e.target.value})}
                     placeholder="Enter email address"
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     required
                   />
                 </div>

                 {/* UserProfile Model Fields */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Contact
                     </label>
                     <input
                       type="text"
                       value={addUserForm.contact}
                       onChange={(e) => setAddUserForm({...addUserForm, contact: e.target.value})}
                       placeholder="Enter phone number"
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Gender
                     </label>
                     <select
                       value={addUserForm.gender}
                       onChange={(e) => setAddUserForm({...addUserForm, gender: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     >
                       <option value="">Select Gender</option>
                       <option value="male">Male</option>
                       <option value="female">Female</option>
                       <option value="other">Other</option>
                     </select>
                   </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     Date of Birth
                   </label>
                   <input
                     type="date"
                     value={addUserForm.dob}
                     onChange={(e) => setAddUserForm({...addUserForm, dob: e.target.value})}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                   />
                 </div>

                 {/* User Permissions */}
                 <div className="flex items-center gap-6">
                   <label className="flex items-center">
                     <input
                       type="checkbox"
                       checked={addUserForm.is_active}
                       onChange={(e) => setAddUserForm({...addUserForm, is_active: e.target.checked})}
                       className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                     />
                     <span className="ml-2 text-sm text-gray-700">Active</span>
                   </label>
                   <label className="flex items-center">
                     <input
                       type="checkbox"
                       checked={addUserForm.is_staff}
                       onChange={(e) => setAddUserForm({...addUserForm, is_staff: e.target.checked})}
                       className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                     />
                     <span className="ml-2 text-sm text-gray-700">Staff</span>
                   </label>
                 </div>
               </div>
             </div>
             <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
               <button
                 onClick={() => {
                   setShowAddUserModal(false);
                   setAddUserForm({
                     username: '',
                     first_name: '',
                     last_name: '',
                     email: '',
                     password: '',
                     is_active: true,
                     is_staff: false,
                     contact: '',
                     gender: '',
                     dob: ''
                   });
                 }}
                 className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors duration-200"
               >
                 Cancel
               </button>
               <button 
                 onClick={handleAddUser}
                 disabled={isLoading}
                 className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isLoading ? 'Creating...' : 'Create User'}
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default UserManagementComponent; 